import styled from "styled-components";
import { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import { ClipLoaderCommon, ConfirmComponent, ProcessOrderHeader } from "@components";
import { useAppSelector, AppDispatch, selectCheckout } from "@redux";
import { fetchCartItems } from "./checkout";
import { AppModuleLayout } from "@layouts";
import { breakpoint, ssCheckoutFlow, theme } from "@utilities";
import { NavLink, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { ErrorToast } from "src/Components/toasts/ErrorToast";
import { PATH_BOOKING, PATH_HOME, QuotesBreadcrumbs } from "@routes";
import { IPayNowResponseData, ICartItemData, ISetCustomerData } from "@interfaces";
import _ from "lodash";
import {
    addQuoteCustomersAndReload,
    loadQuoteCustomers,
    loadQuoteDetails,
    loadQuotePreview,
    removeQuoteItemAndFetch,
    selectQuotes,
    submitQuoteItemsAndFetch,
} from "src/Redux/Slices/allQuotes";
import { quoteApi } from "src/Api/quoteApi";
import { CheckoutStep2 } from "./Components/CheckoutStep2";
import { CheckoutStep1 } from "./Components/CheckoutStep1";
import {
    buildQuoteCheckoutContext,
    QuoteStepLabels,
    transformCartItemsToUpdateCartItemsData,
} from "./checkoutHelpers";
import { CheckoutProvider } from "./CheckoutContext";
import { cartApi } from "@api";
import { useStepPersistence } from "./CheckoutStepHelper";

export const QuotesDetails = () => {
    const { quoteId: quoteIdFromParam } = useParams<{ quoteId: string }>();
    const quoteId = quoteIdFromParam!;
    const [searchParams] = useSearchParams();
    const isPending = searchParams.get("isPending") === "true";

    //page hook
    const dispatch = useDispatch<AppDispatch>();
    const navigate = useNavigate();

    const { activeDiscount } = useAppSelector(selectCheckout);
    const { quotesDetails, quotesCustomers, quotesPreview } = useAppSelector(selectQuotes);
    const quoteCustomers = _.get(quotesCustomers, quoteId);
    const currentItems = isPending ? quotesPreview?.items || [] : quotesDetails || [];
    //page state
    const [isDetailsLoading, setDetailsLoading] = useState(true);
    const [isCustomersLoading, setCustomersLoading] = useState(true);
    const [isDeleting, setIsDeleting] = useState(false);
    const [deleteQueue, setDeleteQueue] = useState<
        { id: string | number; type: "cart" | "group" | "product" | "item" }[]
    >([]);

    const hasBookingData = currentItems.some(
        (item) => item.booking_data && item.booking_data.length > 0,
    );

    const hasValidItems = !currentItems.some((item) => {
        // Check product/availability flags
        if (item.is_product_latest === false || item.is_availability_latest === false) {
            return true;
        }
        // Check if booking date has passed
        const bookingDate = new Date(item.booking_date);
        bookingDate.setHours(0, 0, 0, 0);
        const currentDate = new Date();
        currentDate.setHours(0, 0, 0, 0);
        return bookingDate < currentDate;
    });

    const isLoading = isDetailsLoading || isCustomersLoading;

    const { activeStepIndex, setActiveStep } = useStepPersistence(
        hasBookingData && hasValidItems,
        isLoading,
        "quotes-step",
        !hasValidItems,
    );

    //scroll on top
    useEffect(() => {
        const scrollToTop = () => {
            window.scrollTo(0, 0);
        };
        const fetchQuoteDetails = async () => {
            try {
                setDetailsLoading(true);
                if (isPending) {
                    await dispatch(loadQuotePreview(quoteId)).unwrap();
                } else {
                    await dispatch(loadQuoteDetails(quoteId)).unwrap();
                }
            } catch (error) {
                console.error(error);
                ErrorToast({ Message: "Failed to load items" });
            } finally {
                setDetailsLoading(false);
            }
        };

        const fetchQuoteCustomers = async () => {
            try {
                setCustomersLoading(true);
                await dispatch(loadQuoteCustomers(quoteId)).unwrap();
            } catch (error) {
                console.error(error);
                ErrorToast({ Message: "Failed to load customers" });
            } finally {
                setCustomersLoading(false);
            }
        };

        fetchQuoteDetails();
        fetchQuoteCustomers();
        scrollToTop();
    }, []);

    const handleSaveItems = async (
        items: ICartItemData[],
        nextStep: string,
        shouldGoToNextStep: boolean,
    ) => {
        const updateItems = transformCartItemsToUpdateCartItemsData(items);
        await dispatch(
            submitQuoteItemsAndFetch({
                quoteId: quoteId,
                items: updateItems,
            }),
        );
        if (shouldGoToNextStep) {
            // After saving items successfully, we know the cart has items with redeemers
            // Pass overrides to avoid stale closure issues where hasRedeemers might be false
            setActiveStep(
                _.findIndex(QuoteStepLabels, (label) => label.trim().toLowerCase() === nextStep),
                { hasItems: true },
            );
        }
    };

    const handleOrderCreate = async (
        paymentType: string,
        pointsApplied: number,
        commissionApplied?: number | null,
    ) => {
        try {
            const createOrderResponse = await quoteApi.createOrder({
                quoteId,
                paymentType,
                pointsApplied,
                commissionApplied,
            });
            const { is_payment_required, bookingReference } = createOrderResponse.data
                .data as IPayNowResponseData & {
                is_payment_required: boolean;
                bookingReference: string;
            };

            if (!is_payment_required) {
                const res = await cartApi.completeOrder(bookingReference);
                if (res?.data?.code === 200) {
                    navigate(PATH_BOOKING);
                } else {
                    throw new Error("Failed to complete order");
                }
                return;
            }
            const quoteUrl = (
                createOrderResponse.data.data as IPayNowResponseData
            ).payNow.redirectUrl.replaceAll("\\", "");

            // Store checkout flow context for payment failure redirect
            ssCheckoutFlow.setItem({ type: "quote", quoteId });

            window.location.href = quoteUrl;
        } catch (error: any) {
            console.error("Order creation failed:", error);
            ErrorToast({ Message: error?.response?.data?.title });
        }
    };

    const onOrderCreateSuccess = () => {
        dispatch(fetchCartItems());
    };

    const handleHeaderBack = () => {
        if (activeStepIndex > 0) {
            setActiveStep(activeStepIndex - 1);
            return;
        }
        window.history.back();
    };

    const showDeletePopUp = (
        items: { id: string | number; type: "cart" | "group" | "product" | "item" }[],
    ) => {
        setDeleteQueue(items);
    };

    const handleDeleteYes = async () => {
        if (!deleteQueue.length) return;
        setIsDeleting(true);

        try {
            const results = await Promise.allSettled(
                deleteQueue.map(({ id, type }) =>
                    dispatch(removeQuoteItemAndFetch({ quoteId, type, id })).unwrap(),
                ),
            );
            const errors = results.filter((r) => r.status === "rejected");
            if (errors.length) {
                ErrorToast({
                    Message:
                        errors.length > 1
                            ? "Failed to delete some quote items"
                            : "Failed to delete quote item",
                });
            }
        } catch (e) {
            console.error(e);
            ErrorToast({ Message: "Failed to delete quote items" });
        } finally {
            setIsDeleting(false);
            setDeleteQueue([]);
        }
    };

    const onCreateNewCustomer = async (newCustomerData: ISetCustomerData) => {
        const newCustomer = await dispatch(
            addQuoteCustomersAndReload({
                quoteId: Number(quoteId),
                customers: newCustomerData,
            }),
        ).unwrap();
        return newCustomer;
    };

    const renderComponent = () => {
        const activeStep = QuoteStepLabels[activeStepIndex];
        switch (activeStep) {
            case "Checkout":
                return (
                    <CheckoutStep2
                        items={currentItems}
                        moveTo={setActiveStep}
                        onItemsSave={(items, shouldGoToNextStep) =>
                            handleSaveItems(items, "checkout", shouldGoToNextStep)
                        }
                        customers={quoteCustomers?.customers ?? []}
                        onCreateNewCustomer={onCreateNewCustomer}
                        handleOrderCreate={handleOrderCreate}
                        onOrderCreateSuccess={onOrderCreateSuccess}
                        quoteId={quoteId}
                        isQuotesPage={true}
                    />
                );
            case "Items":
                return (
                    <CheckoutStep1
                        handleDeleteItems={(ids) =>
                            showDeletePopUp(ids.map((id) => ({ id, type: "item" })))
                        }
                        handleDeleteProduct={(prodId) => {
                            showDeletePopUp([{ id: prodId, type: "product" }]);
                        }}
                        handleDeleteCart={() => showDeletePopUp([{ id: quoteId, type: "cart" }])}
                        onItemsSave={(items, shouldGoToNextStep) =>
                            handleSaveItems(items, "checkout", shouldGoToNextStep)
                        }
                    />
                );
        }
    };

    return (
        <AppModuleLayout onBack={handleHeaderBack}>
            <ProcessOrderHeader
                stepLabels={QuoteStepLabels}
                activeStepIndex={activeStepIndex}
                breadcrumbs={QuotesBreadcrumbs}
                changeStep={setActiveStep}
            />
            <Main>
                {isDetailsLoading || isCustomersLoading || _.isNil(currentItems) ? (
                    <Container className="wrapper">
                        <Content>
                            <ClipLoaderCommon
                                color={theme.colors.highlight}
                                size={70}
                                margin={150}
                            />
                        </Content>
                    </Container>
                ) : currentItems.length === 0 ? (
                    <Container className="wrapper">
                        <Content>
                            <img
                                width={65}
                                height={65}
                                src="/images/cart/emptyCart.svg"
                                alt="emptyCartLogo"
                            />
                            <p>There are no items in the quote</p>
                            <NavLink to={PATH_HOME}>
                                <Button>Continue Browsing</Button>
                            </NavLink>
                        </Content>
                    </Container>
                ) : (
                    <CheckoutProvider
                        initialItems={currentItems}
                        checkoutContext={buildQuoteCheckoutContext(quoteId)}
                        activeDiscount={activeDiscount?.discount}
                    >
                        {renderComponent()}
                    </CheckoutProvider>
                )}
                <ConfirmComponent
                    itemIds={deleteQueue}
                    isDeleting={isDeleting}
                    onClickOverlay={() => setDeleteQueue([])}
                    onClickBtnYes={handleDeleteYes}
                    onClickBtnNo={() => setDeleteQueue([])}
                >
                    {isDeleting
                        ? "Deleting items from cart"
                        : `Are you sure you want to delete ${
                              deleteQueue.length > 1 ? "these items" : "this item"
                          }?`}
                </ConfirmComponent>
            </Main>
        </AppModuleLayout>
    );
};

const Main = styled.div`
    flex: 1;
    align-items: center;
    background-color: #f2f2f2;

    ${breakpoint.breakTablet`
        padding-bottom: 30px;
        flex: 1;
        display: flex;
        flex-direction: column;
        align-items: center;
    `}

    .clipLoader {
        position: fixed;
        top: 0;
        left: 0;
        bottom: 0;
        right: 0;
        display: flex;
        justify-content: center;
        align-items: center;
        z-index: 10;
        background-color: rgba(0, 0, 0, 0.2);

        .overlay {
            position: absolute;
            top: 0;
            left: 0;
            bottom: 0;
            right: 0;
            background-color: rgba(0, 0, 0, 0.3);
        }

        .loader {
            z-index: 1;
        }
    }
`;

const Container = styled.div`
    background-color: white;
    display: flex;
    flex: 1;
    width: 100%;
    height: 100%;
    justify-content: center;
    align-items: center;
    padding: 30px;
`;

const Content = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    flex-direction: column;
    gap: 14px;
`;

const Button = styled.button`
    border: 1px solid ${theme.colors.main};
    padding: 12px 24px;
    border-radius: 4px;
    color: ${theme.colors.button_text1};
    background: none;
    cursor: pointer;

    &:hover {
        background-color: ${theme.colors.hover};
    }
`;
